import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponseBase } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { ProgressRef, ProgressService } from './dialog/progress.service';
import { ITokenModel, ITokenService, SERVICE_TOKEN } from './token/interface';
import { API_CONFIG } from './config';


@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  
  // トークン情報がリフレッシュ中
  private refreshToking = false;
  private refreshToken$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private injector: Injector,
    private http: HttpClient
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let url = req.url;
    // assetsフォルダをスキップする
    let martcher: RegExp = /assets\//;
    if (martcher.test(url)) return next.handle(req);

    // APIのベースURLを追加する
    url = environment.SERVER_URL + url;

    // Content-Typeを追加する
    const newReq = req.clone({ 
      url: url,
      setHeaders: {'Content-Type':'application/json; charset=utf-8'} as const, 
    });

    let ref: ProgressRef | null = null;
    ref = this.injector.get(ProgressService).show();
    return next.handle(newReq).pipe(
      mergeMap((ev) => {
        // リクエストエラーの共通処理
        if (ev instanceof HttpResponseBase) {
          return this.handleData(ev, newReq, next, ref);
        }
        return of(ev);
      }),
      catchError((err: HttpErrorResponse) => this.handleData(err, newReq, next, ref)),
    );
  }

  private handleData(ev: HttpResponseBase, req: HttpRequest<any>, next: HttpHandler, ref :ProgressRef | null): Observable<any> {
    // ローディング終了
    if (ref != null) {
      ref.close();
    }
    // HTTPステータスコード
    switch (ev.status) {
      case 200:
        break;
      case 403:
        break;
      case 401:
        if (ev instanceof HttpErrorResponse){
          let response = ev as HttpErrorResponse
          if(response.error.msg == 'トークンの有効期限が切れました') {
            console.log('トークンの有効期限が切れました');
            // トークン期限切れエラー
            return this.tryRefreshToken(ev, req, next);
          }
        }
        break;
      case 400:
      case 500:
        break;
    }
    if (ev instanceof HttpErrorResponse) {
      return throwError(ev);
    } else {
      return of(ev);
    }
  }

  /**
   * トークンをリフレッシュする
   */
  private tryRefreshToken(ev: HttpResponseBase, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // 既にリフレッシュ中のため、以降のリクエストは待機状態に変更し、結果が返されるまでリクエストが再開される
    if (this.refreshToking) {
      return this.refreshToken$.pipe(
        filter((v) => !!v),
        take(1),
        switchMap(() => next.handle(this.reAttachToken(req))),
      );
    }
    this.refreshToking = true;
    this.refreshToken$.next(null);

    return this.refreshTokenRequest().pipe(
      switchMap((res: any) => {
        // 以降のリクエストは実行可能の状態になるので、通知する
        this.refreshToking = false;
        this.refreshToken$.next(res);
        // リフレッシュ前のトークン情報
        let token = this.tokenService.get();
        token.accessToken = res.data.newToken;
        // トークンを更新する
        this.tokenService.set(token);
        // アクセストークンを更新し、もう一度リクエストする
        return next.handle(this.reAttachToken(req));
      }),
      catchError((err) => {
        this.refreshToking = false;
        return throwError(err);
      }),
    );
  }

  /**
   * アクセストークンを更新する
   */
  private reAttachToken(req: HttpRequest<any>): HttpRequest<any> {
    const { token_send_key } = API_CONFIG;
    const token = this.tokenService.get()?.accessToken;
    const obj: any = {};
    obj[token_send_key] = token;
    return req = req.clone({
      setHeaders: obj,
    });
  }

  /**
   * トークンリフレッシュAPIを呼び出す
   */
  private refreshTokenRequest(): Observable<any> {
    let token = this.tokenService.get();
    const params = new HttpParams()
    .set('token', token.accessToken);
    return this.http.get<ITokenModel>('/refreshToken', { params })
  }

  private get tokenService(): ITokenService {
    return this.injector.get(SERVICE_TOKEN);
  }

  /**
   * エラー処理
   */
  checkErrorCode(ev: HttpResponseBase): void {
    if (ev instanceof HttpErrorResponse) {
      let response = ev as HttpErrorResponse;
      console.log(response);
    }
  }

}
